import React, { useEffect, useMemo, useState } from "react";
import { Container, Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Spinner } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import CommonDataTable from "../../common/DataTable";
import { getNewsColumns } from "../../Components/Common/columns/News";
import DeleteDailog from "../../Components/Common/DeleteDailog";
import { fetchAllNews, deleteNewsById, updateNewsById, createNewsItem, getNewsById, bulkDeleteNews, } from "../../slices/thunks";
import useToast from "../../Components/Hooks/useToast";

const News = () => {
  const dispatch = useDispatch();

  const [filterParams, setFilterParams] = useState({
    page: 1,
    perPage: 10,
    limit: 10,
    search: "",
    orderBy: "createdAt",
    order: "desc",
  });

  const news = useSelector((state) => state.News?.news || []);
  const totalRows = useSelector((state) => state.News?.totalRows || 0);
  const loading = useSelector((state) => state.News?.loading || false);

  const initialFormState = {
    _id: "",
    title: "",
    description: "",
    imageUrl: "",
    updatedAt: null,
  };

  const [form, setForm] = useState(initialFormState);
  const [previewImage, setPreviewImage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);

  const showToastMessage = useToast();

  const uploadFileToServer = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/upload`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const result = await response.json();
      return result;
    } catch (error) {
      throw new Error(`Upload failed: ${error.message}`);
    }
  };

  const buildMediaUrl = useMemo(() => {
    return (path, cacheKey) => {
      if (!path) return "";
      const cacheSuffix = cacheKey ? `?v=${cacheKey}` : `?v=${Date.now()}`;

      if (/^https?:\/\//i.test(path)) {
        return `${path}${path.includes("?") ? "&" : "?"}${cacheSuffix.slice(1)}`;
      }

      const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
      const baseUrl = process.env.REACT_APP_API_URL || "";
      return `${baseUrl}${normalizedPath}${cacheSuffix}`;
    };
  }, []);

  const toastFallbacks = {
    success: {
      create: "News created successfully!",
      update: "News updated successfully!",
      delete: "News deleted successfully!",
      bulkDelete: "Selected news items deleted successfully!",
    },
    error: {
      create: "Failed to create news. Please try again.",
      update: "Failed to update news. Please try again.",
      delete: "Failed to delete news. Please try again.",
      fileSize: "File size too large! Maximum 5MB allowed.",
      fileType: "Invalid file type! Only images are allowed.",
      bulkDelete: "Failed to delete news items. Please try again.",
      noSelection: "Please select at least one news item to delete.",
    },
  };

  const fetchData = async (params = {}) => {
    const updated = {
      ...filterParams,
      ...params,
      limit: params.perPage || filterParams.limit,
      page: params.page || params.currentPage || filterParams.page,
    };

    setFilterParams(updated);
    dispatch(fetchAllNews(updated));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRowSelected = (rows) => {
    setSelectedRows(rows);
  };

  const handleDeleteNews = async (event, id) => {
    event.preventDefault();
    event.stopPropagation();

    setDeleteLoading(id);

    try {
      const result = await dispatch(deleteNewsById(id));
      if (result?.ok) {
        showToastMessage(
          "success",
          result?.message || toastFallbacks.success.delete
        );
        fetchData();
      } else {
        showToastMessage(
          "error",
          result?.message || toastFallbacks.error.delete
        );
      }
    } catch (error) {
      showToastMessage(
        "error",
        error?.message || toastFallbacks.error.delete
      );
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleBulkDeleteNews = () => {
    if (selectedRows.length === 0) {
      showToastMessage("error", toastFallbacks.error.noSelection);
      return;
    }

    setConfirmMessage(`Are you sure you want to delete ${selectedRows.length} news items?`);
    setConfirmAction(() => handleBulkDeleteNewsConfirm);
    setShowConfirmPopup(true);
  };

  const handleBulkDeleteNewsConfirm = async () => {
    setIsBulkDeleting(true);
    const ids = selectedRows.map(row => row._id);

    try {
      const result = await dispatch(bulkDeleteNews(ids));
      if (result?.ok) {
        showToastMessage(
          "success",
          result?.message || toastFallbacks.success.bulkDelete
        );
        setSelectedRows([]);
        fetchData();
      } else {
        showToastMessage(
          "error",
          result?.message || toastFallbacks.error.bulkDelete
        );
      }
    } catch (error) {
      showToastMessage(
        "error",
        error?.message || toastFallbacks.error.bulkDelete
      );
    } finally {
      setIsBulkDeleting(false);
      setShowConfirmPopup(false);
    }
  };

  const handleEditNews = async (row) => {
    setIsEdit(true);
    setModalOpen(true);
    setModalLoading(true);

    try {
      const res = await dispatch(getNewsById(row._id));
      const data = res?.payload?.data || row;

      const updatedAt = data.updatedAt || data.modifiedAt || data.createdAt || Date.now();
      setForm({
        _id: data._id,
        title: data.title,
        description: data.description,
        imageUrl: data.mediaUrl || data.imageUrl || "",
        updatedAt: updatedAt,
      });

      if (data.mediaUrl) {
        setPreviewImage(buildMediaUrl(data.mediaUrl, updatedAt));
      } else {
        setPreviewImage(row.imageUrl || "");
      }

    } catch (error) {
      showToastMessage(
        "error",
        error?.message || toastFallbacks.error.load
      );
      setForm({
        _id: row._id,
        title: row.title,
        description: row.description,
        imageUrl: row.imageUrl,
        updatedAt: row.updatedAt || row.modifiedAt || row.createdAt || Date.now(),
      });
      setPreviewImage(row.imageUrl);
    } finally {
      setModalLoading(false);
    }
  };

  const handleAddNews = () => {
    setIsEdit(false);
    setForm(initialFormState);
    setPreviewImage("");
    setModalOpen(true);
  };

  const handleViewNews = async (row) => {
    try {
      const res = await dispatch(getNewsById(row._id));
      const payload = res?.payload?.data || row;
      setViewData(payload);
    } catch (error) {
      showToastMessage("error", error?.message || toastFallbacks.error.view);
      setViewData(row);
    }
    setViewModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim() || !form.description.trim()) {
      showToastMessage("error", "Title and description are required");
      return;
    }

    setModalLoading(true);

    try {
      let payload;

      if (form.imageUrl instanceof File) {
        if (isEdit) {
          const formData = new FormData();
          formData.append("title", form.title);
          formData.append("description", form.description);
          formData.append("media", form.imageUrl);
          payload = formData;
        } else {
          const uploadResponse = await uploadFileToServer(form.imageUrl);
          if (uploadResponse.status === 1 && uploadResponse.data?.fileUrl) {
            payload = {
              title: form.title,
              description: form.description,
              mediaUrl: uploadResponse.data.fileUrl,
            };
            showToastMessage("success", uploadResponse.message || "Image uploaded successfully!");
          } else {
            throw new Error(uploadResponse.message || "File upload failed");
          }
        }
      } else {
        payload = {
          title: form.title,
          description: form.description,
          mediaUrl: typeof form.imageUrl === "string" ? form.imageUrl : "",
        };
      }

      const result = isEdit
        ? await dispatch(updateNewsById(form._id, payload))
        : await dispatch(createNewsItem(payload));

      if (result?.ok) {
        const fallback = isEdit
          ? toastFallbacks.success.update
          : toastFallbacks.success.create;
        showToastMessage("success", result?.message || fallback);
        setModalOpen(false);
        setForm(initialFormState);
        setPreviewImage("");
        fetchData();
      } else {
        const fallback = isEdit
          ? toastFallbacks.error.update
          : toastFallbacks.error.create;
        showToastMessage("error", result?.message || fallback);
      }
    } catch (error) {
      const fallback = isEdit
        ? toastFallbacks.error.update
        : toastFallbacks.error.create;
      showToastMessage(
        "error",
        error?.message || `Failed to ${isEdit ? "update" : "create"} news.`
      );
    } finally {
      setModalLoading(false);
    }
  };

  const columns = getNewsColumns({
    onEditClick: handleEditNews,
    onDeleteClick: handleDeleteNews,
    onViewClick: handleViewNews,
    deleteLoading: deleteLoading,
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToastMessage("error", toastFallbacks.error.fileSize);
      e.target.value = "";
      return;
    }

    const allowedExtensions = ["png", "jpg", "jpeg"];
    const extension = file.name?.split(".").pop()?.toLowerCase();
    const mimeAllowed = ["image/png", "image/jpg", "image/jpeg"];

    if (!allowedExtensions.includes(extension) || !mimeAllowed.includes(file.type)) {
      showToastMessage("error", toastFallbacks.error.fileType);
      e.target.value = "";
      return;
    }

    setForm({ ...form, imageUrl: file, updatedAt: Date.now() });
    setPreviewImage(URL.createObjectURL(file));
  };

  const handleModalClose = () => {
    if (!modalLoading) {
      setModalOpen(false);
      setForm(initialFormState);
      setPreviewImage("");
    }
  };

  const handleViewModalClose = () => {
    setViewModalOpen(false);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="News" pageTitle="News" />

          <CommonDataTable
            onRowClicked={handleViewNews}
            pointerOnHover
            highlightOnHover
            columns={columns}
            data={news}
            totalRows={totalRows}
            loading={loading}
            fetchData={fetchData}
            filterParams={filterParams}
            searchEnable
            checkboxEnabled={true}
            showAddButton
            buttonName="Add News"
            addOnClickMethod={handleAddNews}
            showExportButton
            exportFileName="news"
            showBulkDeleteButton={true}
            bulkDelete={handleBulkDeleteNews}
            selectedRows={selectedRows}
            bulkSelected={handleRowSelected}
            isBulkDeleting={isBulkDeleting}
          />

          <Modal isOpen={modalOpen} toggle={handleModalClose} size="md">
            <ModalHeader
              toggle={!modalLoading ? handleModalClose : undefined}
              className="modal-title text-body fw-semibold modal-fixed-header-footer"
            >
              {isEdit ? "Edit News" : "Add News"}
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
              <ModalBody>
                <FormGroup>
                  <Label for="title">Title *</Label>
                  <Input
                    id="title"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    required
                    placeholder="Enter news title"
                    disabled={modalLoading}
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="description">Description *</Label>
                  <Input
                    id="description"
                    type="textarea"
                    rows="5"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    required
                    placeholder="Enter news description"
                    disabled={modalLoading}
                  />
                </FormGroup>

                <FormGroup>
                  <Label for="image">Upload Image</Label>
                  <Input
                    id="image"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="mb-2"
                    disabled={modalLoading}
                  />

                  {(previewImage || form.imageUrl) && (
                    <div className="mt-3">
                      <div className="position-relative d-inline-block">
                        <img
                          src={
                            previewImage
                              ? previewImage
                              : typeof form.imageUrl === "string"
                                ? buildMediaUrl(
                                  form.imageUrl,
                                  form.updatedAt || Date.now()
                                )
                                : form.imageUrl
                          }
                          alt="Preview"
                          style={{
                            width: "200px",
                            height: "150px",
                            borderRadius: "8px",
                            objectFit: "cover",
                            border: "2px solid #dee2e6",
                          }}
                        />
                      </div>
                    </div>
                  )}
                </FormGroup>
              </ModalBody>

              <ModalFooter>
                <Button color="secondary" onClick={handleModalClose} disabled={modalLoading}>
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={modalLoading}>
                  {modalLoading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {isEdit ? "Updating..." : "Saving..."}
                    </>
                  ) : (
                    isEdit ? "Update" : "Save"
                  )}
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

          <Modal isOpen={viewModalOpen} toggle={handleViewModalClose} size="md" className="modal-fixed-header-footer">
            <ModalHeader
              toggle={handleViewModalClose}
              className="modal-title text-body fw-semibold"
            >
              News Details
            </ModalHeader>
            <ModalBody>
              {viewData && (
                <div className="view-news-content">
                  <div className="text-center mb-4">
                    {viewData.mediaUrl && (
                      <img
                        src={buildMediaUrl(
                          viewData.mediaUrl,
                          viewData.updatedAt || viewData.modifiedAt || Date.now()
                        )}
                        alt="News"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "400px",
                          objectFit: "contain",
                          borderRadius: "8px",
                          border: "1px solid #ddd"
                        }}
                      />
                    )}
                  </div>

                  <div className="news-details">
                    <div className="mb-3">
                      <strong className="text-muted">Title:</strong>
                      <p className="mt-1">{viewData.title}</p>
                    </div>

                    <div className="mb-3">
                      <strong className="text-muted">Description:</strong>
                      <p className="mt-1" style={{ whiteSpace: 'pre-wrap' }}>{viewData.description}</p>
                    </div>

                    <div className="mb-3">
                      <strong className="text-muted">Published:</strong>
                      <p className="mt-1">
                        {viewData.createdAt ? new Date(viewData.createdAt).toLocaleString() : "-"}
                      </p>
                    </div>

                    {viewData.author && (
                      <div className="mb-3">
                        <strong className="text-muted">Author:</strong>
                        <p className="mt-1">
                          {viewData.author.firstname} {viewData.author.lastname}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </ModalBody>

            <ModalFooter>
              <Button color="secondary" onClick={handleViewModalClose}>
                Close
              </Button>
            </ModalFooter>
          </Modal>

          {showConfirmPopup && confirmAction && (
            <DeleteDailog
              isOpen={showConfirmPopup}
              title="Confirm Bulk Deletion"
              message={confirmMessage}
              confirmLabel={isBulkDeleting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Deleting...
                </>
              ) : "Yes, Delete All"}
              cancelLabel="Cancel"
              confirmColor="danger"
              icon={isBulkDeleting ? "loading" : "warning"}
              onConfirm={() => {
                confirmAction();
              }}
              onCancel={() => {
                if (!isBulkDeleting) {
                  setShowConfirmPopup(false);
                }
              }}
              loading={isBulkDeleting}
              disabled={isBulkDeleting}
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default News;